import React, { useMemo, useEffect, useRef } from 'react'
import { css } from '@emotion/core'
import { H1, H2, useContainerWidth, Separator, LayoutBand, Card, Grid, Alert, Skeleton, colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useIsHelping } from '@fs/zion-user'
import { usePermission } from '@fs/zion-permissions'
import { usePreference, setPreference } from '@fs/zion-preferences'
import { useFeatureFlag } from '@fs/zion-flags'
import AllTreesData from '../tree-import/components/cets/AllTreesData'
import { useAllTreeGroups } from '../tree-import/components/cets/AllTreeGroupsProvider'
import UploadGedcomCard from '../tree-import/components/manage-trees/UploadGedcomCard'
import ManageTreeGroup, { ManageTreeGroupSkeleton } from '../tree-import/components/manage-trees/ManageTreeGroup'
import GenealogyMobileTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyMobileTable'
import GenealogyTable from '../tree-import/components/genealogy-trees/genealogy-table/GenealogyTable'
import GenealogyTreesProvider, {
  useGenealogyTreesContext,
} from '../tree-import/components/genealogy-trees/GenealogyTreesProvider'
import JoinGroupOverlay from '../components/join-group-overlay/JoinGroupOverlay'
import GroupLimitsProvider from '../providers/GroupLimitsProvider'
import { filterAndSortGroups } from '../helpers/filterAndSortGroups'
import SkeletonAnimationSyncDecorator from '../components/SkeletonAnimationSyncDecorator'

const pageContainerCss = css`
  min-height: calc(100vh - var(--header-height, 0px));
`

type ManageTreesContentProps = {
  isMobile: boolean
}

function ManageTreesContent({ isMobile }: ManageTreesContentProps): JSX.Element {
  const { t } = useTranslation()
  const { groups, canAccessCET: canAccessCetAndIsOn, canAccessFGT, isPermittedToAccessCET } = useGroupsContext()
  const { uploadedFiles, failedFiles } = useAllTreeGroups()
  const [isMember] = usePermission('FtMemberUiPermission')
  const userTreesPreference = usePreference('tree.cetTest')
  const hasSetPreferenceRef = useRef(false)

  const { sortedFamilyGroups, ownedTrees, sharedTrees, processingFiles } = useMemo(
    () => filterAndSortGroups(groups, uploadedFiles),
    [groups, uploadedFiles]
  )

  const familyGroupTreeGroups = sortedFamilyGroups.filter(
    (group) => group.types.includes('PRIVATE_PERSON_SHARING') && group.treeIds
  )

  const hasUserTrees = ownedTrees?.length > 0 || sharedTrees?.length > 0

  // Set preference once per mount if user has CET access, has trees, and preference not set
  useEffect(() => {
    if (canAccessCetAndIsOn && hasUserTrees && !userTreesPreference && !hasSetPreferenceRef.current) {
      hasSetPreferenceRef.current = true
      setPreference('tree.cetTest', true)
    }
  }, [canAccessCetAndIsOn, hasUserTrees, userTreesPreference])

  // Show upload card user is permitted to access CET or is a member, has CET access and the researcher flag is turned on, and there are no owned trees or failed files
  const showUploadGedcomCard =
    (isPermittedToAccessCET || isMember) &&
    canAccessCetAndIsOn &&
    !ownedTrees.length &&
    !failedFiles.length &&
    !processingFiles.length

  // Show Trees You Own section if user has CET access OR owns trees
  const showTreesYouOwn =
    canAccessCetAndIsOn || ownedTrees.length > 0 || processingFiles.length > 0 || failedFiles.length > 0

  return (
    <Grid gutters="xs">
      {/* 1. Groups With Trees - Show if user has FGT groups */}
      {canAccessFGT && familyGroupTreeGroups.length > 0 && (
        <ManageTreeGroup
          groupTitle={t('tree-import.manage-trees.family-groups-heading')}
          groups={familyGroupTreeGroups}
          isMobile={isMobile}
        />
      )}

      {/* 2. Trees You Own - Show upload card or owned trees */}
      {showTreesYouOwn && showUploadGedcomCard && <UploadGedcomCard />}

      {showTreesYouOwn && !showUploadGedcomCard && (
        <ManageTreeGroup
          groupTitle={t('tree-import.manage-trees.owned-groups-heading')}
          groups={ownedTrees}
          isMobile={isMobile}
          isOwnedTreesGroup
          processingFiles={processingFiles}
          failedFiles={failedFiles}
        />
      )}

      {/* 3. Trees Shared With You - Show if user has shared trees */}
      {sharedTrees.length > 0 && (
        <ManageTreeGroup
          groupTitle={t('tree-import.manage-trees.shared-groups-heading')}
          groups={sharedTrees}
          isMobile={isMobile}
        />
      )}
    </Grid>
  )
}

type GenealogyTreesCardProps = {
  isMobile: boolean
}

function GenealogyTreesCard({ isMobile }: GenealogyTreesCardProps): JSX.Element | null {
  const { t } = useTranslation()
  const { trees, error: genealogyError } = useGenealogyTreesContext()
  const newSettingsPageOn = useFeatureFlag('treeWeb_groupManagement_newSettingsPage').isOn

  // Return no contents for genealogies when flag is on and there are no trees and no error
  if (newSettingsPageOn && (!trees || trees.length === 0) && !genealogyError) {
    return null
  }

  return (
    <Card margins="xs" edges="border" backgroundColor={colors.gray00}>
      <Grid guttersY="sm">
        <H2 size="H5">{t('tree-import.manage-trees.genealogy-trees-heading')}</H2>
        {genealogyError && (
          <Alert dense type="error">
            {t('tree-import.upload-trees.error.503.web_service_down_error')}
          </Alert>
        )}
        {trees && trees.length > 0 && (isMobile ? <GenealogyMobileTable /> : <GenealogyTable />)}
        {/* TODO: This alert should be removed once the newSettingsPageOn flag is kept and phase 2 changes are fully implemented */}
        {(!trees || trees.length === 0) && !genealogyError && (
          <Alert dense type="help">
            {t('tree-import.manage-trees.no-genealogy-trees')}
          </Alert>
        )}
      </Grid>
    </Card>
  )
}

export default function ManageTreesPage(): JSX.Element {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })
  const isHelping = useIsHelping()
  const { groups, loading, getAndSetGroups } = useGroupsContext()

  // In helper mode, groups data won't load (GroupsProvider skips InnerGroupsProvider),
  // so only show the genealogy trees section for the helpee
  if (isHelping) {
    return (
      <div css={pageContainerCss}>
        <LayoutBand marginY="md">
          <H1 size="H4">{t('tree-import.manage-trees.heading')}</H1>
          <Separator size="xs" />
          <GenealogyTreesProvider>
            <GenealogyTreesCard isMobile={isMobile} />
          </GenealogyTreesProvider>

          {/* Space for Help Assistant */}
          <Separator size="lg" />
        </LayoutBand>
      </div>
    )
  }

  if (loading || !groups) {
    return <ManageTreesPageSkeleton />
  }

  return (
    <GroupLimitsProvider>
      <div css={pageContainerCss}>
        <JoinGroupOverlay onJoinGroup={() => getAndSetGroups?.(true)} />
        <LayoutBand marginY="md">
          <Grid gutters="xs">
            <H1 size="H4">{t('tree-import.manage-trees.heading')}</H1>

            <AllTreesData>
              <ManageTreesContent isMobile={isMobile} />
            </AllTreesData>

            {/* 4. Uploaded to Genealogies */}
            <GenealogyTreesProvider>
              <GenealogyTreesCard isMobile={isMobile} />
            </GenealogyTreesProvider>
          </Grid>

          {/* Space for Help Assistant */}
          <Separator size="lg" />
        </LayoutBand>
      </div>
    </GroupLimitsProvider>
  )
}

export function ManageTreesPageSkeleton(): JSX.Element {
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, lg: false })

  return (
    <SkeletonAnimationSyncDecorator>
      <LayoutBand marginY="md">
        <Skeleton.H4 />
        <Separator size="xs" />
        <ManageTreeGroupSkeleton isMobile={isMobile} />

        {/* Space for Help Assistant */}
        <Separator size="lg" />
      </LayoutBand>
    </SkeletonAnimationSyncDecorator>
  )
}
